import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { AddressAutocompleteDocument } from "#app/lib/data";
import type { AddressSuggestion } from "#app/lib/hooks/address-search-types";
import { useDebouncedValue } from "#app/lib/hooks/use-debounced-value";

/** Below this many characters Google Places returns noise, so don't spend a call. */
const MIN_CHARS = 3;
/** Typing settles for this long before a call goes out — each one is billable. */
const DEBOUNCE_MS = 300;

/**
 * Address suggestions for a caller-owned query string, via core's
 * `addressAutocomplete` (a server-side Google Places proxy).
 *
 * Deliberately server-side. The one component that needed this used to inject
 * its own `maps.googleapis.com/maps/api/js?libraries=places` script and drive
 * `google.maps.places.AutocompleteService` in the browser. That second loader
 * raced @vis.gl/react-google-maps' APIProvider (which mounts only with the map)
 * and, when both mounted in the same commit, left two competing Maps bootstraps
 * that broke the map canvas outright. One loader, and the Places key stays on
 * the server.
 *
 * The caller owns `query` (it belongs to their text input); this owns the
 * debounce and the fetch. Suggestions are derived from the query result rather
 * than mirrored into state, so a fast edit can't leave stale predictions on
 * screen.
 */
export function useAddressAutocomplete(query: string): {
  suggestions: AddressSuggestion[];
  /** True while the debounce is settling or the request is in flight. */
  searching: boolean;
} {
  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);
  const trimmed = debouncedQuery.trim();
  const tooShort = trimmed.length < MIN_CHARS;

  // Hoisted — an inline object is a new reference every render, which makes
  // Apollo re-subscribe in a loop.
  const variables = useMemo(() => ({ query: trimmed }), [trimmed]);

  const { data, loading } = useQuery(AddressAutocompleteDocument, {
    variables,
    skip: tooShort,
  });

  const suggestions = useMemo<AddressSuggestion[]>(() => {
    if (tooShort) return [];
    return (data?.addressAutocomplete ?? []).map((s) => ({
      placeId: s.placeId,
      description: s.description,
    }));
  }, [data, tooShort]);

  // Report searching through the gap between the live query and the debounced
  // one too, or the list looks idle-but-empty while a call is pending.
  const settling = query.trim() !== trimmed;
  return {
    suggestions,
    searching: !tooShort && (loading || settling),
  };
}
